import { router } from 'expo-router';
import { useCallback } from 'react';

import { AlertDialog, Button, Text, createMqStyles, tokens, useMqSelect } from '@fhs/ui';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

const TITLE = 'Log In to Save Favorites';
const BODY = 'Log in or create an account to save this location to your favorites.';

interface SaveFavoritesLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Guest favorite-store dialog for the v2 locator. AlertDialog's string heading is always
 * Header Two; mobile uses Header One and desktop uses Header Two, so the title is a custom node.
 */
export function SaveFavoritesLoginModal({ isOpen, onClose }: SaveFavoritesLoginModalProps) {
  const styles = useSaveFavoritesLoginModalMqStyles();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);

  const goToSignIn = useCallback(() => {
    onClose();
    router.navigate(routes.signIn);
  }, [onClose]);

  return (
    <AlertDialog
      visible={isOpen}
      accessibilityLabel={TITLE}
      headingContent={<Text.Heading type={isDesktop ? 'two' : 'one'}>{TITLE}</Text.Heading>}
      bodyContent={<Text.Paragraph size="md">{BODY}</Text.Paragraph>}
      hideCloseIcon
      onClose={onClose}
      onRequestClose={onClose}
      style={styles.dialog}
      testID="save-favorites-login-modal"
      actions={
        <>
          <Button
            size="xl"
            type="solid"
            onPress={goToSignIn}
            style={styles.actionButton}
            testID="save-favorites-login-confirm"
          >
            <Button.Text>Log In / Sign Up</Button.Text>
          </Button>
          <Button
            size="xl"
            type="outline"
            onPress={onClose}
            style={styles.actionButton}
            testID="save-favorites-login-cancel"
          >
            <Button.Text>Cancel</Button.Text>
          </Button>
        </>
      }
    />
  );
}

const useSaveFavoritesLoginModalMqStyles = createMqStyles({
  dialog: {
    $base: {
      shadowColor: tokens.colors.$black,
      shadowOffset: { width: 0, height: 20 },
      shadowOpacity: 0.1,
      shadowRadius: 30,
      elevation: 10,
    },
    $gteDesktop: {
      borderRadius: 12,
    },
  },
  actionButton: {
    $base: {
      width: '100%',
    },
    $gteDesktop: {
      flex: 1,
    },
  },
});
